import { useEffect, useState } from 'react';
import PageLoader from '../components/PageLoader';
import { useParams, useNavigate } from 'react-router-dom';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePointsEnabled } from '../hooks/usePointsEnabled';
import RemoveEmployeeModal from '../components/RemoveEmployeeModal';
import KudosModal from '../components/KudosModal';
import { firstNameLastInitial } from '@crewcast/shared';
import '../styles/profile.css';

interface EmployeeProfile {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  companyId: string;
  profilePicture: string | null;
  status: string;
  createdAt?: string;
  lastLoginAt?: string | null;
  points?: number;
}

type SubmissionStats = {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
};

export default function EmployeeProfilePage() {
  const { token, user } = useAuth();
  const pointsEnabled = usePointsEnabled();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<EmployeeProfile | null>(null);
  const [submissionStats, setSubmissionStats] = useState<SubmissionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeSuccess, setRemoveSuccess] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [showKudosModal, setShowKudosModal] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchEmployeeProfile(userId);
      fetchEmployeeStats(userId);
    } else {
      setError('Missing employee id');
      setLoading(false);
    }
  }, [userId]);

  const fetchEmployeeProfile = async (id: string) => {
    try {
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${authConfig.apiUrl}/api/admin/employees/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch employee profile');
      }

      setEmployee(data.employee);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch employee profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeStats = async (id: string) => {
    try {
      if (!token) return;

      const res = await fetch(`${authConfig.apiUrl}/api/admin/submissions/user-stats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userIds: [id] }),
      });

      const data = await res.json();
      if (res.ok) {
        const stats = data?.stats?.[id];
        if (stats) {
          setSubmissionStats({
            total: stats.total ?? 0,
            approved: stats.approved ?? 0,
            pending: stats.pending ?? 0,
            rejected: stats.rejected ?? 0,
          });
        } else {
          setSubmissionStats({ total: 0, approved: 0, pending: 0, rejected: 0 });
        }
      }
    } catch (e) {
      // Non-fatal; profile page still works
      console.warn('Failed to fetch employee submission stats', e);
    }
  };

  const handleRemove = async () => {
    if (!userId || !token) return;

    setRemoving(true);
    setRemoveError(null);

    try {
      const response = await fetch(`${authConfig.apiUrl}/api/admin/employees/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to remove employee');
      }

      // Flash the success state inside the modal, then return to the list
      setRemoving(false);
      setRemoveSuccess(true);
      setTimeout(() => navigate('/employees'), 1300);
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove employee');
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <PageLoader />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="profile-container">
        <div className="profile-error">
          {error || 'Employee profile not found'}
        </div>
        <button className="profile-button-secondary" onClick={() => navigate('/employees')}>
          Back to Employees
        </button>
      </div>
    );
  }

  const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unnamed User';

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1 className="profile-title">{fullName}</h1>
        <p className="profile-subtitle">
          {employee.role === 'employee' ? 'Employee' : employee.role} · {user?.companyName ?? employee.companyId}
        </p>
      </div>

      <section className="profile-section">
        <div className="profile-picture-section">
          <div className="profile-picture-preview">
            {employee.profilePicture ? (
              <img
                src={employee.profilePicture}
                alt={fullName}
                className="profile-picture-img"
              />
            ) : (
              <div className="profile-picture-placeholder">
                {employee.firstName?.[0]?.toUpperCase() || '?'}
              </div>
            )}
          </div>
          <div className="profile-picture-info">
            <p className="profile-picture-label">Employee Profile</p>
            <p className="profile-picture-hint">
              View basic details about this team member. Additional activity and submissions will
              appear here in a future update.
            </p>
          </div>
        </div>

        <form className="profile-form">
          <div className="profile-form-group">
            <label className="profile-label">First Name</label>
            <input
              className="profile-input"
              type="text"
              value={employee.firstName}
              disabled
            />
          </div>

          <div className="profile-form-group">
            <label className="profile-label">Last Name</label>
            <input
              className="profile-input"
              type="text"
              value={employee.lastName}
              disabled
            />
          </div>

          <div className="profile-form-group">
            <label className="profile-label">Email</label>
            <input
              className="profile-input"
              type="email"
              value={employee.email}
              disabled
            />
          </div>

          <div className="profile-form-group">
            <label className="profile-label">Status</label>
            <input
              className="profile-input"
              type="text"
              value={employee.status === 'invited' ? 'Invited' : 'Active'}
              disabled
            />
          </div>
        </form>
      </section>

      <section className="profile-section">
        <div className="profile-section-header">
          <h2 className="profile-section-title">Activity Summary</h2>
        </div>
        <div className="employee-profile-stats-grid">
          {pointsEnabled && (
          <div className="employee-profile-stat-card">
            <div className="employee-profile-stat-value">{employee.points ?? 0}</div>
            <div className="employee-profile-stat-label">Points</div>
          </div>
          )}
          <div className="employee-profile-stat-card">
            <div className="employee-profile-stat-value">{submissionStats?.total ?? 0}</div>
            <div className="employee-profile-stat-label">Submissions</div>
          </div>
          <div className="employee-profile-stat-card">
            <div className="employee-profile-stat-value">{submissionStats?.approved ?? 0}</div>
            <div className="employee-profile-stat-label">Approved</div>
          </div>
          <div className="employee-profile-stat-card">
            <div className="employee-profile-stat-value">{submissionStats?.pending ?? 0}</div>
            <div className="employee-profile-stat-label">Pending</div>
          </div>
          <div className="employee-profile-stat-card">
            <div className="employee-profile-stat-value">{submissionStats?.rejected ?? 0}</div>
            <div className="employee-profile-stat-label">Rejected</div>
          </div>
          <div className="employee-profile-stat-card">
            <div className="employee-profile-stat-value">
              {employee.lastLoginAt ? new Date(employee.lastLoginAt).toLocaleDateString() : '—'}
            </div>
            <div className="employee-profile-stat-label">Last Login</div>
          </div>
        </div>
      </section>

      <div className="profile-actions-row">
        <button className="profile-button-secondary" onClick={() => navigate('/employees')}>
          Back to Employees
        </button>
        {employee.status === 'active' && (
          <button className="profile-button" onClick={() => setShowKudosModal(true)}>
            Send Kudos
          </button>
        )}
        <button
          className="profile-button-danger"
          onClick={() => {
            setRemoveError(null);
            setShowRemoveModal(true);
          }}
        >
          Remove Employee
        </button>
      </div>

      {showKudosModal && (
        <KudosModal
          employee={{ userId: employee.userId, name: firstNameLastInitial(employee) }}
          source="employee-profile"
          onClose={() => setShowKudosModal(false)}
          onSent={(r) => {
            if (r.pointsAwarded > 0) {
              setEmployee((e) => e && { ...e, points: (e.points ?? 0) + r.pointsAwarded });
            }
          }}
        />
      )}

      {showRemoveModal && (
        <RemoveEmployeeModal
          name={fullName}
          status={employee.status}
          loading={removing}
          success={removeSuccess}
          error={removeError}
          onConfirm={handleRemove}
          onClose={() => setShowRemoveModal(false)}
        />
      )}
    </div>
  );
}


